'use client';
// Bookmark React Query hooks
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { bookmarkApi, GetBookmarksParams } from '@/services/api/bookmark.api';
import {
  setBookmarkedEventIds,
  setBookmarkState,
  toggleBookmarkedEventId,
  useAppDispatch,
} from '@/store';

export const BOOKMARK_QUERY_KEYS = {
  all: ['bookmarks'] as const,
  lists: (userId?: string) => [...BOOKMARK_QUERY_KEYS.all, 'list', userId].filter(Boolean),
  list: (params: GetBookmarksParams, userId?: string) =>
    [...BOOKMARK_QUERY_KEYS.lists(userId), params] as const,
};

const BOOKMARK_PAGE_LIMIT = 20;

/**
 * Infinite-scroll query for the authenticated user's bookmarks.
 */
export const useBookmarksList = (options?: { enabled?: boolean; limit?: number }) => {
  const { user } = useAuth();
  const userId = user?.walletAddress || user?.email; // Use whatever unique ID is available

  return useInfiniteQuery({
    queryKey: BOOKMARK_QUERY_KEYS.lists(userId),
    queryFn: ({ pageParam = 0 }) =>
      bookmarkApi.getBookmarks({
        limit: options?.limit || BOOKMARK_PAGE_LIMIT,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (lastPage.data.length < (options?.limit || BOOKMARK_PAGE_LIMIT)) return undefined;
      return fetched;
    },
    initialPageParam: 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false && !!userId,
  });
};

/**
 * Mutation to toggle a bookmark. Invalidates the bookmark list on success.
 */
export const useToggleBookmark = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const userId = user?.walletAddress || user?.email;

  return useMutation({
    mutationFn: (eventId: string) => bookmarkApi.toggleBookmark(eventId),
    onMutate: async (eventId: string) => {
      // Optmistically update in Redux for instant feedback
      dispatch(toggleBookmarkedEventId(eventId));

      // Cancel any outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: BOOKMARK_QUERY_KEYS.lists(userId) });
    },
    onError: (_error, eventId) => {
      dispatch(toggleBookmarkedEventId(eventId));
    },
    onSuccess: (data, eventId) => {
      if (data && data.data && typeof data.data.bookmarked === 'boolean') {
        dispatch(setBookmarkState({ id: eventId, bookmarked: data.data.bookmarked }));
      }
      queryClient.invalidateQueries({ queryKey: BOOKMARK_QUERY_KEYS.lists(userId) });
    },
  });
};
